/**
 * Shows metadata about transactions scanned during the last insight refresh.
 */
import React from 'react';
import { Database, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { useRegion } from '../../contexts/RegionContext';
import type { InsightScanSummary } from '../../types/insights.types';

interface InsightScanSummaryPanelProps {
  summary: InsightScanSummary;
  className?: string;
}

export function InsightScanSummaryPanel({
  summary,
  className = '',
}: InsightScanSummaryPanelProps): React.ReactElement {
  const [expanded, setExpanded] = React.useState(false);
  const { formatCurrency, formatDate } = useRegion();

  const formatIsoDate = (iso: string | null) => {
    if (!iso) return '—';
    try {
      return formatDate(iso);
    } catch {
      return iso;
    }
  };

  return (
    <div
      className={`rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden ${className}`}
      data-testid="insight-scan-summary"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 focus-ring"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Database className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0" aria-hidden />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Data scanned
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {summary.transaction_count} transaction{summary.transaction_count === 1 ? '' : 's'} ·{' '}
              {formatIsoDate(summary.window_start_date)} – {formatIsoDate(summary.window_end_date)}
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-gray-500 flex-shrink-0" aria-hidden />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" aria-hidden />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-4 border-t border-gray-100 dark:border-gray-700 space-y-4">
          {summary.truncated && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden />
              <span>
                Analysis used the first {summary.scan_limit} transactions in this window. Results may
                not include all activity if you have more than that.
              </span>
            </div>
          )}

          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <dt className="text-gray-500 dark:text-gray-400 text-xs">Spending txns</dt>
              <dd className="font-semibold text-gray-900 dark:text-gray-100">{summary.debit_count}</dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400 text-xs">Income txns</dt>
              <dd className="font-semibold text-gray-900 dark:text-gray-100">{summary.credit_count}</dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400 text-xs">Transfers</dt>
              <dd className="font-semibold text-gray-900 dark:text-gray-100">{summary.transfer_count}</dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400 text-xs">Categories</dt>
              <dd className="font-semibold text-gray-900 dark:text-gray-100">
                {summary.categories_with_spending}
              </dd>
            </div>
          </dl>

          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 px-3 py-2">
              <dt className="text-gray-500 dark:text-gray-400 text-xs">Total spending</dt>
              <dd className="font-semibold text-gray-900 dark:text-gray-100">
                {formatCurrency(summary.total_spending)}
              </dd>
            </div>
            <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 px-3 py-2">
              <dt className="text-gray-500 dark:text-gray-400 text-xs">Total income</dt>
              <dd className="font-semibold text-gray-900 dark:text-gray-100">
                {formatCurrency(summary.total_income)}
              </dd>
            </div>
            <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 px-3 py-2">
              <dt className="text-gray-500 dark:text-gray-400 text-xs">Net</dt>
              <dd
                className={`font-semibold ${
                  summary.net_amount >= 0
                    ? 'text-emerald-700 dark:text-emerald-400'
                    : 'text-red-700 dark:text-red-400'
                }`}
              >
                {formatCurrency(summary.net_amount)}
              </dd>
            </div>
          </dl>

          <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
            <p>
              Transaction dates in scan:{' '}
              <span className="text-gray-700 dark:text-gray-300">
                {formatIsoDate(summary.earliest_transaction_date)} –{' '}
                {formatIsoDate(summary.latest_transaction_date)}
              </span>
            </p>
            <p>
              Income history (for irregular-income checks):{' '}
              <span className="text-gray-700 dark:text-gray-300">
                {summary.income_history_transaction_count} transactions over ~6 months
              </span>
            </p>
            <p>
              Active savings goals included:{' '}
              <span className="text-gray-700 dark:text-gray-300">{summary.active_savings_goals}</span>
            </p>
          </div>

          {summary.top_spending_categories.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                Top spending categories
              </h3>
              <ul className="space-y-1.5">
                {summary.top_spending_categories.map((cat) => (
                  <li
                    key={cat.name}
                    className="flex items-center justify-between text-sm gap-2"
                  >
                    <span className="text-gray-800 dark:text-gray-200 truncate">{cat.name}</span>
                    <span className="text-gray-500 dark:text-gray-400 flex-shrink-0 text-xs">
                      {cat.transaction_count} txn{cat.transaction_count === 1 ? '' : 's'} ·{' '}
                      {formatCurrency(cat.total_spent)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
